// src/components/resumo/quiz/QuizInlineAccordion.tsx
// 🎯 Cartão de Quiz — expande inline no lugar do marcador, sem modal/overlay
// Progresso é controlado pelo componente pai (ResumoReadPage), que persiste
// em localStorage — assim as respostas sobrevivem à troca de página.

import React, { useState } from 'react';
import { ListChecks, ChevronDown, CheckCircle2, XCircle, RotateCcw, Eye, Lock } from 'lucide-react';
import { QuizBadgeData, QuizAlternative, QuizProgress } from './quiz.types';
import './QuizCard.css';
import './QuizInlineAccordion.css';

interface QuizInlineAccordionProps {
    quizId: string;
    quizData: QuizBadgeData;
    darkMode: boolean;
    progress: QuizProgress;
    onProgressChange: (quizId: string, progress: QuizProgress) => void;
    // Prévia pública (ApostilaPreviewPage, via ResumoReadPage isPreview): o
    // card continua aparecendo e abrindo/fechando normalmente (demonstra que
    // a apostila tem quiz), mas o corpo interativo (perguntas/respostas) vira
    // um teaser — responder é recurso da assinatura, não da amostra.
    locked?: boolean;
    onLockedCtaClick?: () => void;
}

const QuizInlineAccordion: React.FC<QuizInlineAccordionProps> = ({ quizId, quizData, darkMode, progress, onProgressChange, locked = false, onLockedCtaClick }) => {
    const [expanded, setExpanded] = useState(false);
    // Navegação de revisão ("ver minhas respostas") é só um modo de leitura da
    // UI — não precisa persistir, some ao recolher/remontar o cartão.
    const [reviewing, setReviewing] = useState(false);
    const { selectedAnswers, score, currentQuestionIndex, completed } = progress;
    const showingQuestion = !completed || reviewing;

    const totalQuestions = quizData.questions.length;
    const currentQuestion = quizData.questions[currentQuestionIndex];
    const isAnswered = selectedAnswers[currentQuestionIndex] !== undefined;
    const selectedAnswer = selectedAnswers[currentQuestionIndex];
    const isCorrect = currentQuestion ? selectedAnswer === currentQuestion.correctAnswer : false;
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    if (!currentQuestion) return null;

    const handleSelectAnswer = (key: QuizAlternative) => {
        if (isAnswered) return;

        const isCorrectAnswer = key === currentQuestion.correctAnswer;
        onProgressChange(quizId, {
            ...progress,
            selectedAnswers: { ...selectedAnswers, [currentQuestionIndex]: key },
            score: isCorrectAnswer ? score + 1 : score,
        });
    };

    const handleNext = () => {
        if (isLastQuestion) {
            if (reviewing) {
                setReviewing(false);
                return;
            }
            onProgressChange(quizId, { ...progress, completed: true });
            return;
        }
        onProgressChange(quizId, { ...progress, currentQuestionIndex: currentQuestionIndex + 1 });
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            onProgressChange(quizId, { ...progress, currentQuestionIndex: currentQuestionIndex - 1 });
        }
    };

    const handleReset = () => {
        setReviewing(false);
        onProgressChange(quizId, {
            selectedAnswers: {},
            score: 0,
            currentQuestionIndex: 0,
            completed: false,
        });
    };

    const handleStartReview = () => {
        setReviewing(true);
        onProgressChange(quizId, { ...progress, currentQuestionIndex: 0 });
    };

    const handleExitReview = () => {
        setReviewing(false);
    };

    const progressPct = ((currentQuestionIndex + 1) / totalQuestions) * 100;

    const getAlternativeLetter = (key: QuizAlternative): string => key.toUpperCase();

    const getAlternativeClass = (key: QuizAlternative): string => {
        if (!isAnswered) return 'quiz-inline-alternative';

        if (key === currentQuestion.correctAnswer) {
            return 'quiz-inline-alternative quiz-inline-alternative-correct';
        }
        if (key === selectedAnswer && key !== currentQuestion.correctAnswer) {
            return 'quiz-inline-alternative quiz-inline-alternative-wrong';
        }
        return 'quiz-inline-alternative quiz-inline-alternative-disabled';
    };

    const alternativeKeys: QuizAlternative[] = ['a', 'b', 'c', 'd'];

    const countText = locked
        ? `${totalQuestions} pergunta${totalQuestions !== 1 ? 's' : ''} · disponível na versão completa`
        : completed
            ? `Concluído · ${score}/${totalQuestions} acerto${totalQuestions !== 1 ? 's' : ''}`
            : expanded
                ? `${totalQuestions} pergunta${totalQuestions !== 1 ? 's' : ''}`
                : `${totalQuestions} pergunta${totalQuestions !== 1 ? 's' : ''} · clique para responder`;

    const resultMessage = score === totalQuestions
        ? 'Mandou muito bem — acertou tudo!'
        : score === 0
            ? 'Vale a pena revisar esta página antes de tentar de novo.'
            : 'Bom trabalho! Revise a página para fixar o que ainda passou batido.';

    return (
        <div
            className={`quiz-card quiz-card-read ${darkMode ? 'dark' : ''} ${expanded ? 'expanded' : ''}`}
            contentEditable={false}
        >
            <button
                type="button"
                className="quiz-card-trigger"
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
            >
                <span className="quiz-card-icon">
                    <ListChecks size={14} strokeWidth={1.75} />
                </span>
                <span className="quiz-card-text">
                    <span className="quiz-card-title">Teste seu conhecimento</span>
                    <span className="quiz-card-count">{countText}</span>
                </span>
                <ChevronDown size={18} className="quiz-card-chevron" />
            </button>

            <div className="quiz-inline-collapse">
                <div className="quiz-inline-collapse-inner">
                    {expanded && (
                        <div className="quiz-inline-body">
                            {locked ? (
                                <div className="quiz-inline-locked">
                                    <span className="quiz-inline-locked-icon">
                                        <Lock size={22} strokeWidth={1.75} />
                                    </span>
                                    <p className="quiz-inline-locked-title">Quiz disponível na versão completa</p>
                                    <p className="quiz-inline-locked-desc">
                                        Assine para responder as {totalQuestions} pergunta{totalQuestions !== 1 ? 's' : ''} desta página e ver sua nota na hora.
                                    </p>
                                    {onLockedCtaClick && (
                                        <button type="button" className="quiz-inline-locked-btn" onClick={onLockedCtaClick}>
                                            Quero assinar
                                        </button>
                                    )}
                                </div>
                            ) : !showingQuestion ? (
                                <div className="quiz-inline-results">
                                    <div className="quiz-inline-results-score">{score} / {totalQuestions}</div>
                                    <p className="quiz-inline-results-message">{resultMessage}</p>
                                    <div className="quiz-inline-results-actions">
                                        <button
                                            type="button"
                                            className="quiz-inline-nav-btn quiz-inline-review-btn"
                                            onClick={handleStartReview}
                                        >
                                            <Eye size={16} />
                                            Ver minhas respostas
                                        </button>
                                        <button
                                            type="button"
                                            className="quiz-inline-nav-btn quiz-inline-nav-btn-next quiz-inline-reset-btn"
                                            onClick={handleReset}
                                        >
                                            <RotateCcw size={16} />
                                            Refazer quiz
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="quiz-inline-progress-bar">
                                        <div className="quiz-inline-progress-fill" style={{ width: `${progressPct}%` }} />
                                    </div>
                                    <div className="quiz-inline-progress-row">
                                        <span className="quiz-inline-progress-text">
                                            Questão {currentQuestionIndex + 1} de {totalQuestions}
                                        </span>
                                        {reviewing ? (
                                            <button type="button" className="quiz-inline-review-exit" onClick={handleExitReview}>
                                                Ver resultado
                                            </button>
                                        ) : (
                                            <span className="quiz-inline-score">{score} / {totalQuestions}</span>
                                        )}
                                    </div>

                                    <div className="quiz-inline-question">{currentQuestion.question}</div>

                                    <div className="quiz-inline-alternatives">
                                        {alternativeKeys.map((key) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className={getAlternativeClass(key)}
                                                onClick={() => handleSelectAnswer(key)}
                                                disabled={isAnswered}
                                            >
                                                <span className="quiz-inline-alternative-letter">
                                                    {getAlternativeLetter(key)}
                                                </span>
                                                <span className="quiz-inline-alternative-text">
                                                    {currentQuestion.alternatives[key]}
                                                </span>
                                                {isAnswered && key === currentQuestion.correctAnswer && (
                                                    <CheckCircle2 size={18} className="quiz-inline-icon-correct" />
                                                )}
                                                {isAnswered && key === selectedAnswer && key !== currentQuestion.correctAnswer && (
                                                    <XCircle size={18} className="quiz-inline-icon-wrong" />
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {isAnswered && (
                                        <div className={`quiz-inline-feedback ${isCorrect ? 'correct' : 'wrong'}`}>
                                            {isCorrect ? (
                                                <>
                                                    <CheckCircle2 size={18} />
                                                    <span>Resposta correta!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle size={18} />
                                                    <span>Resposta incorreta. A alternativa correta é {getAlternativeLetter(currentQuestion.correctAnswer)}.</span>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {isAnswered && currentQuestion.explanation && currentQuestion.explanation.trim() && (
                                        <div className="quiz-inline-explanation">
                                            <span className="quiz-inline-explanation-label">Explicação</span>
                                            <p>{currentQuestion.explanation}</p>
                                        </div>
                                    )}

                                    <div className="quiz-inline-nav">
                                        <button
                                            type="button"
                                            className="quiz-inline-nav-btn"
                                            onClick={handlePrevious}
                                            disabled={currentQuestionIndex === 0}
                                        >
                                            Anterior
                                        </button>
                                        <button
                                            type="button"
                                            className="quiz-inline-nav-btn quiz-inline-nav-btn-next"
                                            onClick={handleNext}
                                            disabled={!isAnswered}
                                        >
                                            {isLastQuestion ? (reviewing ? 'Voltar ao resultado' : 'Ver resultado') : 'Próxima'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizInlineAccordion;
